import React, { useState, useCallback, useEffect } from 'react';
import { Globe, CheckCircle, XCircle, Loader2, ExternalLink, RefreshCw } from 'lucide-react';
import { apiRequest } from '../config/api';
import { useToast } from '../context/ToastContext';
import { generatePublishHTML } from './PageBuilder/PreviewCanvas/generatePreviewHTML';

/**
 * SiteDeploymentPanel — Manages subdomain claiming and site deployment.
 *
 * Props:
 *   site         — The site object (from API)
 *   siteSections — Array of site sections (nav, footer)
 *   onSiteUpdate — Callback when site data changes (e.g., after claiming subdomain)
 */
export default function SiteDeploymentPanel({ site, siteSections, onSiteUpdate }) {
  const { showSuccess, showError } = useToast();
  const [subdomain, setSubdomain] = useState(site?.subdomain || '');
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState(null); // null | true | false
  const [availabilityMessage, setAvailabilityMessage] = useState('');
  const [claiming, setClaiming] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deployStatus, setDeployStatus] = useState(site?.deployment_status || 'not_deployed');
  const [lastDeployedAt, setLastDeployedAt] = useState(site?.last_deployed_at);

  useEffect(() => {
    setSubdomain(site?.subdomain || '');
    setDeployStatus(site?.deployment_status || 'not_deployed');
    setLastDeployedAt(site?.last_deployed_at);
  }, [site]);

  const checkAvailability = useCallback(async () => {
    if (!subdomain || subdomain.length < 3) {
      setAvailable(null);
      setAvailabilityMessage('');
      return;
    }
    setChecking(true);
    try {
      const response = await apiRequest(`/sites/subdomain/check/${subdomain.toLowerCase()}`);
      const data = await response.json();
      setAvailable(data.available);
      setAvailabilityMessage(data.message);
    } catch (err) {
      setAvailable(null);
      setAvailabilityMessage('Error checking availability');
    } finally {
      setChecking(false);
    }
  }, [subdomain]);

  const claimSubdomain = useCallback(async () => {
    if (!subdomain) return;
    setClaiming(true);
    try {
      const response = await apiRequest(`/sites/${site.id}/subdomain`, {
        method: 'POST',
        body: JSON.stringify({ subdomain: subdomain.toLowerCase() }),
      });
      if (response.ok) {
        const updatedSite = await response.json();
        showSuccess('Subdomain claimed successfully');
        setAvailable(null);
        setAvailabilityMessage('');
        if (onSiteUpdate) onSiteUpdate(updatedSite);
      } else {
        const err = await response.json();
        showError(err.detail || 'Failed to claim subdomain');
      }
    } catch (err) {
      showError('Failed to claim subdomain');
    } finally {
      setClaiming(false);
    }
  }, [subdomain, site?.id, showSuccess, showError, onSiteUpdate]);

  const deploySite = useCallback(async () => {
    if (!site?.subdomain) {
      showError('Please claim a subdomain first');
      return;
    }

    setDeploying(true);
    setDeployStatus('deploying');

    try {
      // 1. Fetch all pages for this site
      const pagesRes = await apiRequest(`/sites/${site.id}/pages`);
      if (!pagesRes.ok) throw new Error('Failed to fetch site pages');
      const sitePages = await pagesRes.json();

      if (sitePages.length === 0) {
        showError('No pages to deploy');
        setDeploying(false);
        setDeployStatus(site.deployment_status || 'not_deployed');
        return;
      }

      // 2. Fetch sections for each page
      const pagesWithSections = await Promise.all(
        sitePages.map(async (page) => {
          const sectionsRes = await apiRequest(`/pages/${page.id}/sections`);
          const sections = sectionsRes.ok ? await sectionsRes.json() : [];
          return { ...page, sections };
        })
      );

      // 3. Build page ID to slug map for internal link resolution
      const pageIdToSlugMap = {};
      pagesWithSections.forEach(page => {
        if (page.id && page.slug) {
          pageIdToSlugMap[page.id] = page.slug;
        }
      });

      // 4. Generate HTML for each page
      const deployPages = pagesWithSections.map((page, index) => {
        const sections = page.sections || [];
        const pageConfig = {
          page_background_color: page.content?.page_background_color || '#ffffff',
        };
        const publishConfig = {
          title: page.title || site.name,
          metaDescription: page.description || site.meta_description || '',
          faviconUrl: site.favicon_url || '',
          siteSections: siteSections || [],
          pageIdToSlugMap,
        };

        const html = generatePublishHTML(sections, pageConfig, publishConfig);

        return {
          slug: page.slug || `page-${index}`,
          html,
          is_homepage: page.is_homepage || false,
        };
      });

      // 5. Send to backend
      const response = await apiRequest(`/sites/${site.id}/deploy`, {
        method: 'POST',
        body: JSON.stringify({ pages: deployPages }),
      });

      if (response.ok) {
        const result = await response.json();
        setDeployStatus('deployed');
        setLastDeployedAt(result.last_deployed_at);
        showSuccess(`Site deployed successfully! (${deployPages.length} page${deployPages.length > 1 ? 's' : ''})`);
        if (onSiteUpdate) {
          onSiteUpdate({ ...site, deployment_status: 'deployed', last_deployed_at: result.last_deployed_at });
        }
      } else {
        const err = await response.json();
        setDeployStatus('failed');
        showError(err.detail || 'Deployment failed');
      }
    } catch (err) {
      setDeployStatus('failed');
      showError('Deployment failed: ' + err.message);
    } finally {
      setDeploying(false);
    }
  }, [site, siteSections, showSuccess, showError, onSiteUpdate]);

  const hasClaimed = !!site?.subdomain;
  const liveUrl = hasClaimed ? `https://${site.subdomain}.fasttttt.com` : null;

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Globe className="w-4 h-4 text-gray-500" />
        <h3 className="text-sm font-semibold text-gray-700">Site Deployment</h3>
      </div>

      {/* Subdomain Input */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Subdomain</label>
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={subdomain}
            onChange={(e) => {
              setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
              setAvailable(null);
              setAvailabilityMessage('');
            }}
            onBlur={checkAvailability}
            placeholder="my-site"
            disabled={hasClaimed}
            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
          />
          <span className="text-xs text-gray-400 whitespace-nowrap">.fasttttt.com</span>
        </div>

        {/* Availability indicator */}
        {checking && (
          <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
            <Loader2 className="w-3 h-3 animate-spin" />
            Checking...
          </div>
        )}
        {!checking && available === true && (
          <div className="flex items-center gap-1 mt-1 text-xs text-green-600">
            <CheckCircle className="w-3 h-3" />
            {availabilityMessage}
          </div>
        )}
        {!checking && available === false && (
          <div className="flex items-center gap-1 mt-1 text-xs text-red-600">
            <XCircle className="w-3 h-3" />
            {availabilityMessage}
          </div>
        )}

        {/* Claim button */}
        {!hasClaimed && (
          <button
            onClick={claimSubdomain}
            disabled={claiming || !subdomain || subdomain.length < 3 || available === false}
            className="mt-2 w-full px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {claiming ? 'Claiming...' : 'Claim Subdomain'}
          </button>
        )}
      </div>

      {/* Deploy Section */}
      {hasClaimed && (
        <div className="border-t border-gray-200 pt-4 space-y-3">
          {/* Status */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Status</span>
            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
              deployStatus === 'deployed' ? 'bg-green-100 text-green-700' :
              deployStatus === 'deploying' ? 'bg-blue-100 text-blue-700' :
              deployStatus === 'failed' ? 'bg-red-100 text-red-700' :
              'bg-gray-100 text-gray-600'
            }`}>
              {deployStatus === 'deploying' && <Loader2 className="w-3 h-3 animate-spin" />}
              {deployStatus === 'deployed' && <CheckCircle className="w-3 h-3" />}
              {deployStatus === 'failed' && <XCircle className="w-3 h-3" />}
              {deployStatus === 'not_deployed' ? 'Not deployed' :
               deployStatus.charAt(0).toUpperCase() + deployStatus.slice(1)}
            </span>
          </div>

          {/* Last deployed */}
          {lastDeployedAt && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Last deployed</span>
              <span className="text-xs text-gray-600">
                {new Date(lastDeployedAt).toLocaleString()}
              </span>
            </div>
          )}

          {/* Live URL */}
          {deployStatus === 'deployed' && liveUrl && (
            <a
              href={liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700"
            >
              <ExternalLink className="w-3 h-3" />
              {liveUrl}
            </a>
          )}

          {/* Deploy button */}
          <button
            onClick={deploySite}
            disabled={deploying}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm bg-brand-600 text-white rounded-md hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {deploying ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Deploying...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                {deployStatus === 'deployed' ? 'Re-deploy' : 'Deploy Site'}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
